import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import FeedCard from "../components/FeedCard";
import Avatar from "../components/Avatar";
import { useAuth } from "../context/AuthContext";
import { FaChevronLeft, FaChevronRight, FaHeart, FaComment } from "react-icons/fa";
import { toast } from "../lib/toast";

function getRecipeImageUrl(recipe) {
  const firstImage = (recipe.images || []).find((media) => media?.image);
  return firstImage?.image || null;
}

const Feed = () => {
  const { currentUser } = useAuth();
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [posting, setPosting] = useState(false);
  const [trending, setTrending] = useState([]);
  const scrollRef = useRef(null);
  const loadMoreRef = useRef(null);
  const navigate = useNavigate();

  const fetchFeed = async (pageToLoad = 1) => {
    const isFirstPage = pageToLoad === 1;
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const res = await axiosInstance.get("/api/feed/", { params: { page: pageToLoad } });
      setPosts((prev) => {
        if (isFirstPage) return res.data.results;
        const seen = new Set(prev.map((post) => post.id));
        const nextPosts = res.data.results.filter((post) => !seen.has(post.id));
        return [...prev, ...nextPosts];
      });
      setHasNext(!!res.data.next);
    } catch (err) {
      console.error("Failed to load feed", err);
    } finally {
      if (isFirstPage) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const fetchTrending = async () => {
    try {
      const res = await axiosInstance.get("/api/recipes/trending/");
      setTrending(res.data);
    } catch (err) {
      console.error("Failed to load trending", err);
    }
  };

  useEffect(() => {
    fetchTrending();
  }, []);

  useEffect(() => {
    fetchFeed(page);
  }, [page, reloadKey]);

  useEffect(() => {
    if (!hasNext || loading || loadingMore || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNext, loading, loadingMore]);

  const handlePostStatus = async (e) => {
    e.preventDefault();
    if (!statusText.trim()) return;
    setPosting(true);
    try {
      await axiosInstance.post("/api/feed/create/", { message: statusText });
      setStatusText("");
      setPosts([]);
      setPage(1);
      setReloadKey((prev) => prev + 1);
      toast.success("Status posted.");
    } catch (err) {
      console.error("Failed to post status", err);
      toast.error("Failed to post status.");
    } finally {
      setPosting(false);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const amount = 200;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -amount : amount,
        behavior: "smooth",
      });
    }
  };

  if (loading && page === 1) {
    return <div className="p-6 type-muted">Loading feed...</div>;
  }

  return (
    <div className="flex-1 p-6 max-w-2xl mx-auto">
      <h1 className="type-page-title mb-6">Your Feed</h1>

      {/* Trending This Week */}
      {trending.length > 0 && (
        <div className="mb-6">
          <h2 className="type-eyebrow mb-3">
            Trending This Week
          </h2>
          <div className="relative">
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 rounded-full p-1.5 shadow-md -ml-2"
            >
              <FaChevronLeft className="w-3 h-3 text-gray-600 dark:text-gray-300" />
            </button>
            <div
              ref={scrollRef}
              className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
            >
              {trending.map((recipe) => {
                const img = getRecipeImageUrl(recipe);
                return (
                  <div
                    key={recipe.id}
                    onClick={() => navigate(`/recipes/${recipe.id}`)}
                    className="w-44 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/50 cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={recipe.title}
                        className="w-full h-32 object-cover"
                      />
                    ) : (
                      <div className="w-full h-32 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500 text-xs">
                        No image
                      </div>
                    )}
                    <div className="p-2">
                      <p className="type-card-title truncate">
                        {recipe.title}
                      </p>
                      <p className="type-muted text-xs truncate">
                        {recipe.owner?.first_name} {recipe.owner?.last_name}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 dark:text-gray-500">
                        <span className="flex items-center gap-1">
                          <FaHeart className="w-3 h-3" /> {recipe.likes_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <FaComment className="w-3 h-3" /> {recipe.comments_count}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 rounded-full p-1.5 shadow-md -mr-2"
            >
              <FaChevronRight className="w-3 h-3 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>
      )}

      {/* Status post bar */}
      <form onSubmit={handlePostStatus} className="bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/50 rounded-lg p-4 mb-6 flex items-center space-x-3">
        <Avatar user={currentUser} size="md" className="flex-shrink-0" />
        <input
          type="text"
          value={statusText}
          onChange={(e) => setStatusText(e.target.value)}
          placeholder="What's on your mind?"
          className="flex-1 bg-gray-100 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <button
          type="submit"
          disabled={!statusText.trim() || posting}
          className="type-button px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {posting ? "Posting..." : "Post"}
        </button>
      </form>

      {posts.length === 0 ? (
        <div className="text-center py-12">
          <p className="type-section-title">Your feed is empty.</p>
          <p className="type-muted mt-2">Follow other users to see their posts here.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map((post) => (
            <FeedCard key={post.id} post={post} />
          ))}
        </div>
      )}

      {(hasNext || loadingMore) && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          <span className="type-muted">
            {loadingMore ? "Loading more posts..." : "Scroll for more"}
          </span>
        </div>
      )}
    </div>
  );
};

export default Feed;
